import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useBalance } from '@app/hooks/user/mybalance';
import { useTradingWallets } from '@app/hooks/user/useTradingWallets';
import { useToken } from '@app/hooks';
import { TRANSFER } from '@app/constants/APIKEYS';
import API from '@/utils/api';
import BigNumber from 'bignumber.js';
import {
  transferToTradingWallet,
  transferFromTradingWallet,
} from '@app/api/wallet';
import CSelect from '@/app/components/Common/CSelect';
import CBottomSheet from '@/app/components/Common/CBottomSheet';
import { useIsMobile } from '@/utils/CalVW';

// Icons from Figma (local paths) - Mobile
const mobileCloseIconUrl =
  '/assets/newHome/copy-trading/close-icon-transfer.svg';
const mobileSearchIconUrl =
  '/assets/newHome/copy-trading/search-icon-transfer.svg';
const mobileSwapIconUrl = '/assets/newHome/copy-trading/swap-icon-transfer.svg';
const mobileLineIconUrl = '/assets/newHome/copy-trading/line-icon-transfer.svg';
const mobileDividerIconUrl =
  '/assets/newHome/copy-trading/divider-line-icon.svg';

// Icons from Figma (local paths) - PC
const pcCloseIconUrl =
  '/assets/newHome/copy-trading/close-icon-transfer-pc.svg';
const pcSearchIconUrl =
  '/assets/newHome/copy-trading/search-icon-transfer-pc.svg';
const pcSwapIconUrl = '/assets/newHome/copy-trading/swap-icon-transfer-pc.svg';
const pcLineIconUrl = '/assets/newHome/copy-trading/line-icon-transfer-pc.svg';
const pcDividerIconUrl =
  '/assets/newHome/copy-trading/divider-line-icon-pc.svg';

// Mobile ModalContent
const MobileModalContent = styled.div`
  background: #0c2323;
  border-radius: 16px 16px 0 0;
  padding: 24px 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// PC ModalContent
const PCModalContent = styled.div`
  background: #0c2323;
  border-radius: 16px;
  padding: 0;
  width: 424px;
  min-height: 694px;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
`;

// Mobile Header
const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// PC Header
const PCHeader = styled.div`
  position: absolute;
  left: 34.5px;
  top: 40px;
  width: 354px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h2`
  font-family: 'Aggro', 'Aggro', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  letter-spacing: -0.36px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

// Mobile FormSection
const MobileFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// PC FormSection
const PCFormSection = styled.div`
  position: absolute;
  left: 34.5px;
  top: 94px;
  width: 354px;
  display: flex;
  flex-direction: column;
  gap: 36px;
`;

const TransferDirectionBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
`;

const DirectionTitle = styled.label`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  text-align: center;
`;

const DirectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  position: relative;
`;

const DirectionRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  height: 50px;
  padding: 10px 20px;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const DirectionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 60px;
  position: relative;
  flex-shrink: 0;
`;

const LabelText = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  white-space: nowrap;
`;

const LabelLine = styled.div`
  width: 1px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.2);
`;

const SelectButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  height: 50px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  gap: 20px;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const SelectText = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const SelectIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Swap 버튼 공통 스타일 (TransferDirectionBox 기준 정중앙)
const SwapButtonContainer = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 42px;
  height: 42px;
  background: #303d3f;
  border: 1px solid #07d993;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    box-shadow: 0 0 10px rgba(7, 217, 147, 0.3);
  }
`;

// Mobile SwapButtonContainer (별칭)
const MobileSwapButtonContainer = SwapButtonContainer;

// PC SwapButtonContainer (별칭)
const PCSwapButtonContainer = SwapButtonContainer;

const SwapIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const CoinSelectButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  height: 50px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const CoinText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CoinSymbol = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

const CoinName = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.24px;
`;

const AmountInput = styled.div`
  border: 1px solid #07d993;
  border-radius: 12px;
  height: 50px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const AmountInputField = styled.input`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
  background: transparent;
  border: none;
  outline: none;
  flex: 1;
  width: 0;

  &::placeholder {
    color: #b3b5bd;
    font-weight: 400;
  }
`;

const AmountRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CurrencyLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
  width: 35px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AllButton = styled.button`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #07d993;
  letter-spacing: -0.28px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 25px;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const BalanceBox = styled.div`
  background: #0d1117;
  border-radius: 12px;
  height: 42px;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BalanceLabel = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #b3b5bd;
  letter-spacing: -0.28px;
`;

const BalanceValue = styled.span`
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.28px;
`;

// Mobile ConfirmButton
const MobileConfirmButton = styled.button`
  width: 100%;
  height: 48px;
  border-radius: 12px;
  border: none;
  background: #07d993;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 40px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// PC ConfirmButton
const PCConfirmButton = styled.button`
  position: absolute;
  bottom: 40px;
  left: 35px;
  width: 354px;
  height: 48px;
  border-radius: 12px;
  border: none;
  background: #07d993;
  color: #0d1117;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 40px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownMenu = styled.div`
  margin-top: 8px;
  background: #0d1117;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 8px;
  position: absolute;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  color: ${(props) => (props.$selected ? '#07d993' : '#ffffff')};
  border-radius: 8px;
  font-family: 'NanumSquareRound', sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.$selected ? '700' : '400')};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const FormGroupWrapper = styled.div`
  position: relative;
`;

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  defaultFrom?: string;  // 기본 보내는 지갑 (예: 'spotwallet', 'copywallet')
  defaultTo?: string;    // 기본 받는 지갑 (예: 'futurewallet', 'copywallet')
}

const walletTypeMap: Record<string, number | 'BOT' | 'COPY'> = {
  spotwallet: 0,
  futurewallet: 1,
  rewardwallet: 7,
  botwallet: 'BOT',
  copywallet: 'COPY',
};

const walletTypeReverseMap: Record<number | 'BOT' | 'COPY', string> = {
  0: 'spotwallet',
  1: 'futurewallet',
  7: 'rewardwallet',
  BOT: 'botwallet',
  COPY: 'copywallet',
};

const TransferModal: React.FC<TransferModalProps> = ({
  open,
  onClose,
  defaultFrom = 'spotwallet',
  defaultTo = 'futurewallet',
}) => {
  const { t } = useTranslation();
  const { data: token } = useToken();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      document.body.style.overflowX = 'hidden';
      return () => {
        document.body.style.overflowX = '';
      };
    }
  }, [open]);

  const [transferFrom, setTransferFrom] = useState(defaultFrom);
  const [transferTo, setTransferTo] = useState(defaultTo);

  // 모달이 열릴 때 기본값으로 리셋
  useEffect(() => {
    if (open) {
      setTransferFrom(defaultFrom);
      setTransferTo(defaultTo);
    }
  }, [open, defaultFrom, defaultTo]);
  const [selectedCoin, setSelectedCoin] = useState('USDT');
  const [transferAmount, setTransferAmount] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);

  const fromType = walletTypeMap[transferFrom];
  const toType = walletTypeMap[transferTo];
  const fromTypeNum = typeof fromType === 'number' ? fromType : 0;

  const { data: balance, mutate: mutateFrom } = useBalance(
    selectedCoin,
    fromTypeNum
  );
  const { mutate: mutateTo } = useBalance(
    selectedCoin,
    typeof toType === 'number' ? toType : 0
  );

  // TransferModal이 열렸을 때만 useTradingWallets API 호출
  const { mutate: mutateTradingWallets } = useTradingWallets({ enabled: open });

  // 선물지갑 → 현물지갑 전송 시 event_balance는 전송 불가하므로 차감
  const availableBalance = new BigNumber(balance?.balance || 0)
    .minus(balance?.lockbalance || 0)
    .minus(
      transferFrom === 'futurewallet' && transferTo === 'spotwallet'
        ? balance?.event_balance || 0
        : 0
    );

  // From options
  const fromOptions = [
    'spotwallet',
    'futurewallet',
    'rewardwallet',
    'botwallet',
    'copywallet',
  ];

  // To options based on From
  // 서버 API 지원 조건:
  // - /transfer: SPOT → BOT/COPY (FUTURES → BOT/COPY 미지원)
  // - /transfer/reverse: BOT/COPY → SPOT/FUTURES
  // - Legacy API: SPOT ↔ FUTURES, REWARD → FUTURES
  const getToOptions = () => {
    if (transferFrom === 'spotwallet') {
      // 현물 → 선물, 봇, 카피 모두 가능
      return ['futurewallet', 'botwallet', 'copywallet'];
    } else if (transferFrom === 'rewardwallet') {
      // 리워드 → 선물만 가능
      return ['futurewallet'];
    } else if (transferFrom === 'futurewallet') {
      // 선물 → 현물만 가능 (봇/카피 전송 서버 미지원)
      return ['spotwallet'];
    } else if (transferFrom === 'botwallet') {
      // 봇 → 현물, 선물 가능
      return ['spotwallet', 'futurewallet'];
    } else if (transferFrom === 'copywallet') {
      // 카피 → 현물, 선물 가능
      return ['spotwallet', 'futurewallet'];
    }
    return ['spotwallet', 'futurewallet'];
  };

  useEffect(() => {
    const availableToOptions = getToOptions();
    if (!availableToOptions.includes(transferTo)) {
      setTransferTo(availableToOptions[0]);
    }
  }, [transferFrom]);

  const handleSwap = () => {
    if (
      transferFrom === 'rewardwallet' ||
      transferFrom === 'botwallet' ||
      transferFrom === 'copywallet' ||
      transferTo === 'botwallet' ||
      transferTo === 'copywallet'
    ) {
      return;
    }
    const temp = transferFrom;
    setTransferFrom(transferTo);
    setTransferTo(temp);
  };

  const handleMaxClick = () => {
    setTransferAmount(availableBalance.toFixed(6));
  };

  const handleTransfer = async () => {
    if (isTransferring) {
      return message.error(t('transferInProgress') || 'Transfer in progress');
    }

    const numAmount = parseFloat(transferAmount);

    if (numAmount <= 0 || isNaN(numAmount)) {
      return message.error(t('invalidAmount') || 'Invalid amount');
    }

    if (numAmount > availableBalance.toNumber()) {
      return message.error(t('insufficientBalance') || 'Insufficient balance');
    }

    setIsTransferring(true);

    const isNewWallet =
      transferFrom === 'botwallet' ||
      transferFrom === 'copywallet' ||
      transferTo === 'botwallet' ||
      transferTo === 'copywallet';

    try {
      if (isNewWallet) {
        // 서버 API 지원 조건 검증:
        // - /transfer: SPOT → BOT/COPY만 지원 (FUTURES → BOT/COPY 미지원)
        // - /transfer/reverse: BOT/COPY → SPOT/FUTURES 지원
        if (
          transferFrom === 'futurewallet' &&
          (transferTo === 'botwallet' || transferTo === 'copywallet')
        ) {
          // 선물 → 봇/카피 전송은 서버에서 미지원
          return message.error(
            t('futuresTransferNotSupported') ||
              '선물지갑에서 봇/카피지갑으로 직접 전송은 지원되지 않습니다. 현물지갑을 경유해 주세요.'
          );
        }

        let response;
        if (transferFrom === 'spotwallet') {
          // 현물 → BOT/COPY 전송 (FUTURES는 위에서 차단됨)
          response = await transferToTradingWallet({
            amount: numAmount,
            fromType: 'SPOT',
            toType: transferTo === 'botwallet' ? 'BOT' : 'COPY',
          });
        } else {
          // BOT/COPY → 현물/선물 전송
          response = await transferFromTradingWallet({
            amount: numAmount,
            fromType: transferFrom === 'botwallet' ? 'BOT' : 'COPY',
            toType: transferTo === 'spotwallet' ? 'SPOT' : 'FUTURES',
          });
        }

        if (response.success) {
          message.success(t('transferSuccess') || 'Transfer completed');
          mutateFrom();
          mutateTo();
          mutateTradingWallets();
          setTransferAmount('');
          onClose();
        } else {
          message.error(
            response.message || t('transferFailed') || 'Transfer failed'
          );
        }
      } else {
        const data = {
          transfer_from: fromType,
          transfer_to: toType,
          coin_type: selectedCoin,
          amount: transferAmount,
        };

        const res = await API.post(TRANSFER, token + '', data);

        if (res.result.result == true) {
          message.success(t('transferSuccess') || 'Transfer completed');
          mutateFrom();
          mutateTo();
          setTransferAmount('');
          onClose();
        } else {
          // 특정 에러 코드 처리
          const errorCode = res?.result?.message;
          if (errorCode === 'ERROR_NOBALANCE_EXCLUDE_EVENT') {
            message.error(
              t('errorNoBalanceExcludeEvent') ||
                '이벤트로 획득한 금액은 현물지갑으로 전송할 수 없습니다.'
            );
          } else {
            message.error(
              t(errorCode) || t('transferFailed') || 'Transfer failed'
            );
          }
        }
      }
    } catch (err: any) {
      console.error('Transfer error:', err);
      message.error(
        err?.message ||
          t('transferError') ||
          'An error occurred during transfer'
      );
    } finally {
      setIsTransferring(false);
    }
  };

  const closeIcon = isMobile ? mobileCloseIconUrl : pcCloseIconUrl;
  const searchIcon = isMobile ? mobileSearchIconUrl : pcSearchIconUrl;
  const swapIcon = isMobile ? mobileSwapIconUrl : pcSwapIconUrl;
  const lineIcon = isMobile ? mobileLineIconUrl : pcLineIconUrl;
  const dividerIcon = isMobile ? mobileDividerIconUrl : pcDividerIconUrl;

  if (isMobile) {
    return (
      <CBottomSheet
        isOpen={open}
        onClose={onClose}
        height="auto"
        sheetContainerStyle={{
          background: '#0c2323',
        }}
      >
        <MobileModalContent>
          <MobileHeader>
            <Title>{t('transfer')}</Title>
            <CloseButton onClick={onClose}>
              <img
                src={closeIcon}
                alt="Close"
                style={{ width: '20px', height: '20px', display: 'block' }}
              />
            </CloseButton>
          </MobileHeader>

          <MobileFormSection>
            <FormGroupWrapper>
              <TransferDirectionBox>
                <DirectionTitle>{t('transferDirection')}</DirectionTitle>
                <DirectionContainer>
                  <DirectionRow>
                    <DirectionLabel>
                      <LabelText>{t('from')}</LabelText>
                      <LabelLine />
                    </DirectionLabel>
                    <CSelect
                      variant="inline"
                      value={transferFrom}
                      options={fromOptions.map((option) => ({
                        value: option,
                        label: t(option),
                      }))}
                      onChange={(value) => {
                        setTransferFrom(value as string);
                      }}
                    />
                  </DirectionRow>

                  <DirectionRow>
                    <DirectionLabel>
                      <LabelText>{t('to')}</LabelText>
                      <LabelLine />
                    </DirectionLabel>
                    <CSelect
                      variant="inline"
                      value={transferTo}
                      options={getToOptions().map((option) => ({
                        value: option,
                        label: t(option),
                      }))}
                      onChange={(value) => {
                        setTransferTo(value as string);
                      }}
                    />
                  </DirectionRow>
                  <MobileSwapButtonContainer onClick={handleSwap}>
                    <SwapIcon>
                      <img
                        src={swapIcon}
                        alt="Swap"
                        style={{
                          width: '24px',
                          height: '24px',
                          display: 'block',
                        }}
                      />
                    </SwapIcon>
                  </MobileSwapButtonContainer>
                </DirectionContainer>
              </TransferDirectionBox>
            </FormGroupWrapper>

            <FormGroupWrapper>
              <FormGroup>
                <Label>{t('coin')}</Label>
                <CSelect
                  variant="deposit"
                  value={selectedCoin}
                  options={['USDT'].map((coin) => ({
                    value: coin,
                    label: coin,
                  }))}
                  onChange={(value) => {
                    setSelectedCoin(value as string);
                  }}
                />
              </FormGroup>
            </FormGroupWrapper>

            <FormGroup>
              <Label>{t('amount')}</Label>
              <AmountInput>
                <AmountInputField
                  type="number"
                  placeholder="0"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  min="0"
                  step="0.000001"
                />
                <AmountRight>
                  <CurrencyLabel>{selectedCoin}</CurrencyLabel>
                  <Divider>
                    <img
                      src={dividerIcon}
                      alt=""
                      style={{ width: '1px', height: '12px', display: 'block' }}
                    />
                  </Divider>
                  <AllButton onClick={handleMaxClick}>ALL</AllButton>
                </AmountRight>
              </AmountInput>
              <BalanceBox style={{ marginTop: '8px' }}>
                <BalanceLabel>{t('availableBalance')}</BalanceLabel>
                <BalanceValue>
                  {availableBalance.toFixed(6)} {selectedCoin}
                </BalanceValue>
              </BalanceBox>
            </FormGroup>
          </MobileFormSection>

          <MobileConfirmButton
            onClick={handleTransfer}
            disabled={isTransferring}
          >
            {isTransferring
              ? t('transferring') || 'Transferring...'
              : t('confirm')}
          </MobileConfirmButton>
        </MobileModalContent>
      </CBottomSheet>
    );
  }

  // PC Version
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={424}
      style={{
        maxWidth: 'none',
      }}
      styles={{
        body: { padding: 0, overflowX: 'hidden' },
        content: {
          background: '#0d1117',
          boxShadow: 'none',
          overflowX: 'hidden',
          width: '424px',
          maxWidth: '424px',
          padding: 0,
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.8)',
        },
      }}
    >
      <PCModalContent>
        <PCHeader>
          <Title>{t('transfer')}</Title>
          <CloseButton onClick={onClose}>
            <img
              src={closeIcon}
              alt="Close"
              style={{ width: '20px', height: '20px', display: 'block' }}
            />
          </CloseButton>
        </PCHeader>

        <PCFormSection>
          <FormGroupWrapper>
            <TransferDirectionBox>
              <DirectionTitle>{t('transferDirection')}</DirectionTitle>
              <DirectionContainer>
                <DirectionRow>
                  <DirectionLabel>
                    <LabelText>{t('from')}</LabelText>
                    <LabelLine />
                  </DirectionLabel>
                  <CSelect
                    variant="inline"
                    value={transferFrom}
                    options={fromOptions.map((option) => ({
                      value: option,
                      label: t(option),
                    }))}
                    onChange={(value) => {
                      setTransferFrom(value as string);
                    }}
                  />
                </DirectionRow>

                <DirectionRow>
                  <DirectionLabel>
                    <LabelText>{t('to')}</LabelText>
                    <LabelLine />
                  </DirectionLabel>
                  <CSelect
                    variant="inline"
                    value={transferTo}
                    options={getToOptions().map((option) => ({
                      value: option,
                      label: t(option),
                    }))}
                    onChange={(value) => {
                      setTransferTo(value as string);
                    }}
                  />
                </DirectionRow>
                <PCSwapButtonContainer onClick={handleSwap}>
                  <SwapIcon>
                    <img
                      src={swapIcon}
                      alt="Swap"
                      style={{
                        width: '24px',
                        height: '24px',
                        display: 'block',
                      }}
                    />
                  </SwapIcon>
                </PCSwapButtonContainer>
              </DirectionContainer>
            </TransferDirectionBox>
          </FormGroupWrapper>

          <FormGroupWrapper>
            <FormGroup>
              <Label>{t('coin')}</Label>
              <CSelect
                variant="deposit"
                value={selectedCoin}
                options={['USDT'].map((coin) => ({
                  value: coin,
                  label: coin,
                }))}
                onChange={(value) => {
                  setSelectedCoin(value as string);
                }}
              />
            </FormGroup>
          </FormGroupWrapper>

          <FormGroup>
            <Label>{t('amount')}</Label>
            <AmountInput>
              <AmountInputField
                type="number"
                placeholder="0"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                min="0"
                step="0.000001"
              />
              <AmountRight>
                <CurrencyLabel>{selectedCoin}</CurrencyLabel>
                <Divider>
                  <img
                    src={dividerIcon}
                    alt=""
                    style={{ width: '1px', height: '12px', display: 'block' }}
                  />
                </Divider>
                <AllButton onClick={handleMaxClick}>ALL</AllButton>
              </AmountRight>
            </AmountInput>
            <BalanceBox style={{ marginTop: '8px' }}>
              <BalanceLabel>{t('availableBalance')}</BalanceLabel>
              <BalanceValue>
                {availableBalance.toFixed(6)} {selectedCoin}
              </BalanceValue>
            </BalanceBox>
          </FormGroup>
        </PCFormSection>

        <PCConfirmButton onClick={handleTransfer} disabled={isTransferring}>
          {isTransferring ? t('transferring') || 'Transferring' : t('confirm')}
        </PCConfirmButton>
      </PCModalContent>
    </Modal>
  );
};

export default TransferModal;
